<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Accounts</h2>
                <p class="list-page__count">{{ accountCountLabel }}</p>
              </div>
              <button type="button" class="list-page__action" :disabled="isLoading" v-on:click="loadAccounts">
                <i :class="['pi', isLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                <span>Refresh</span>
              </button>
            </header>

            <div class="accounts-toolbar">
              <div class="search-box">
                <i class="pi pi-search"></i>
                <input
                  v-model.trim="searchTerm"
                  type="search"
                  class="form-control shadow-none"
                  placeholder="Search by name, email or phone…"
                />
              </div>
              <select
                v-model="roleFilter"
                class="form-control shadow-none role-filter"
                v-on:change="onRoleFilterChange"
              >
                <option value="">All roles</option>
                <option v-for="role in roleOptions" :key="role" :value="role">{{ roleLabel(role) }}</option>
              </select>
            </div>

            <div class="list-page__table">
              <tableLoader v-if="isLoading" message="Loading accounts…" />
              <div v-if="!isLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Account</th>
                      <th scope="col">Role</th>
                      <th scope="col">Subscription</th>
                      <th scope="col">Status</th>
                      <th scope="col">Joined</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(account, index) in filteredAccounts"
                      v-bind:key="account.id || index"
                      class="account-row"
                      v-on:click="viewAccount(account)"
                    >
                      <th scope="row">{{ rowNumber(index) }}</th>
                      <td>
                        <div class="account-cell">
                          <span class="account-avatar">{{ initials(account) }}</span>
                          <div class="account-cell-text">
                            <span class="row-name">{{ $filters.fullName(account.fname, account.lname) }}</span>
                            <span class="account-contact">{{ account.email || account.phoneNumber || '—' }}</span>
                          </div>
                        </div>
                      </td>
                      <td><span class="badge" :class="roleBadgeClass(account.role)">{{ roleLabel(account.role) }}</span></td>
                      <td>
                        <span v-if="planName(account)" class="plan-pill">{{ planName(account) }}</span>
                        <span v-else class="row-muted">No subscription</span>
                      </td>
                      <td>
                        <span v-if="account.isActive" class="badge badge-success">Active</span>
                        <span v-else class="badge badge-danger">Inactive</span>
                      </td>
                      <td>{{ $filters.getDateOnly(account.createdAt) }}</td>
                      <td class="col-actions">
                        <button type="button" class="view-btn" v-on:click.stop="viewAccount(account)">View</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!isLoading && filteredAccounts.length === 0" class="list-page__empty">
                <i class="pi pi-users"></i>
                <div>{{ searchTerm ? 'No accounts match your search.' : 'No accounts found.' }}</div>
              </div>
            </div>

            <pagination
              v-if="!isLoading && metaData"
              :meta-data="metaData"
              :current-page="currentPage"
              :per-page="perPage"
              v-on:change="onPageChange"
            />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import tableLoader from "@/components/Table_Loader.vue";
import pagination from "@/components/Pagination.vue";
import Account from "@/services/account";
import { ROLE_LABELS, USER_ROLES } from "@/utils/enums";
import { ACCOUNT_DETAIL_ROUTE } from "@/router/routes";

const ROLE_BADGE = {
  [USER_ROLES.superAdmin]: "badge-info",
  [USER_ROLES.admin]: "badge-info",
  [USER_ROLES.owner]: "badge-warning",
  [USER_ROLES.user]: "badge-secondary",
};

export default {
  title: "Accounts",
  components: {
    sidenav,
    navbar,
    tableLoader,
    pagination,
  },
  data() {
    return {
      accounts: [],
      metaData: null,
      isLoading: false,
      currentPage: 1,
      perPage: 30,
      searchTerm: "",
      roleFilter: "",
      roleOptions: [USER_ROLES.superAdmin, USER_ROLES.admin, USER_ROLES.owner, USER_ROLES.user],
    };
  },
  computed: {
    // Client-side refinement of the loaded page (the backend has no free-text
    // search), mirroring how the verifications list filters its current page.
    filteredAccounts() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.accounts;
      return this.accounts.filter((a) => {
        const haystack = [
          a.fname,
          a.lname,
          a.email,
          a.phoneNumber,
          this.planName(a),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    accountCountLabel() {
      const total = this.metaData ? this.metaData.totalRecords : this.accounts.length;
      return total === 1 ? "1 account" : `${total} accounts`;
    },
  },
  methods: {
    rowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },
    planName(account) {
      const plan = account && account.organization && account.organization.subscription
        ? account.organization.subscription.subscriptionPlan
        : null;
      return plan ? plan.name : "";
    },
    initials(account) {
      const f = (account.fname || "").charAt(0);
      const l = (account.lname || "").charAt(0);
      return (f + l).toUpperCase() || "?";
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || role || "—";
    },
    roleBadgeClass(role) {
      return ROLE_BADGE[role] || "badge-secondary";
    },
    viewAccount(account) {
      this.$router.push({ name: ACCOUNT_DETAIL_ROUTE, params: { id: account.id } });
    },
    async loadAccounts() {
      this.isLoading = true;
      try {
        const params = { page: this.currentPage, limit: this.perPage };
        if (this.roleFilter) params.role = this.roleFilter;
        const res = await Account.getAccounts(params);
        if (res?.data?.status) {
          this.accounts = res.data.data || [];
          this.metaData = res.data.metaData || null;
        }
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
      this.loadAccounts();
    },
    onRoleFilterChange() {
      this.currentPage = 1;
      this.loadAccounts();
    },
  },
  mounted() {
    this.loadAccounts();
  },
};
</script>
<style scoped>
.accounts-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1 1 320px;
  max-width: 420px;
}

.search-box .pi {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: var(--ehs-color-soft);
  font-size: 0.85rem;
}

.search-box .form-control {
  padding-left: 36px !important;
}

.role-filter {
  width: auto;
  min-width: 150px;
}

.account-row {
  cursor: pointer;
  transition: background 0.12s ease;
}

.account-row:hover {
  background: var(--ehs-color-primary-soft);
}

.account-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.account-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-active);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.account-cell-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-contact {
  font-size: 0.74rem;
  color: var(--ehs-color-soft);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-canvas);
  border: 1px solid var(--ehs-color-border);
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ehs-color-text);
  text-transform: capitalize;
}

.view-btn {
  border: 0;
  background: transparent;
  padding: 4px 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-primary-active);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease;
}

.view-btn:hover {
  color: var(--ehs-color-primary-hover);
  text-decoration: underline;
}

.view-btn:focus-visible {
  outline: none;
  text-decoration: underline;
}
</style>
